import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StripWidget from '../../../../shared/components/features/widget/stripWidget/StripWidget';
import { selectEntitiesByFilters } from '../../../../shared/store/views/entityView';
import { appletsQuery, entitiesByIdsFilter } from '../../../../shared/utils/entitiesFiltersUtils';
import { fetchStudyGroupStudents, getApplets } from '../../../../shared/store/redux/entities-action';
import entitysType from '../../../../shared/enums/entitysType';
import postMessageBridgeTypes from '../../../../shared/enums/postMessageBridgeTypes';
import AppletCarouselItem from './appletCarouselItem/AppletCarouselItem';
import SelectStudyGroupPopup from './selectStudyGroupPopup/SelectStudyGroupPopup';
import pencilCaseImg from '../../../../assests/classroomApplets/pencilCase.png';
import classes from './RegularAppletsWidget.module.css';

const RegularAppletsWidget = ({ studyGroupId }) => {

  const dispatch = useDispatch();
  const { texts } = useSelector(state => state.translate);
  const { user, userDetails } = useSelector(state => state.auth);
  const [pendingApplet, setPendingApplet] = useState(null);
  const applets = useSelector(state => selectEntitiesByFilters(state, appletsQuery));
  const studyGroupFilters = useMemo(() => entitiesByIdsFilter(studyGroupId ? [studyGroupId] : [], entitysType.studyGroup), [studyGroupId]);
  const studyGroup = useSelector(state => selectEntitiesByFilters(state, studyGroupFilters))?.[0];

  const openAppletTab = useCallback((applet, group) => {
    if (!applet.appletUrl) return;
    const appletWindow = window.open(applet.appletUrl, '_blank');
    if (!appletWindow || !applet.requiresContext) return;
    const onMessage = (event) => {
      if (event.origin !== window.config.appletsOrigin) return;
      if (event.data?.type !== postMessageBridgeTypes.APPLET_READY) return;
      window.removeEventListener('message', onMessage);
      appletWindow.postMessage(
        {
          type: postMessageBridgeTypes.APPLET_CONTEXT,
          context: {
            teacher: {
              id: user?.exidentifier,
              name: `${user?.given_name || ''} ${user?.family_name || ''}`.trim(),
              subject: group?.domainName || group?.name || '',
            },
            class: {
              id: group?.id,
              grade: group?.grade || '',
              section: group?.name || '',
              school: {
                id: userDetails?.defaultSchool?.id,
                name: userDetails?.defaultSchool?.name || '',
              },
              lesson: {
                id: null,
                subject: group?.domainName || '',
                startTime: null,
                duration: null,
              },
            },
            members: group?.students ?? [],
          },
        },
        window.config.appletsOrigin
      );
    };
    window.addEventListener('message', onMessage);
  }, [user, userDetails]);

  useEffect(() => {
    dispatch(getApplets());
  }, [dispatch]);

  const openAppletWithStudents = async (applet, group) => {
    if (group.students) {
      openAppletTab(applet, group);
    } else {
      const students = await dispatch(fetchStudyGroupStudents(group.id));
      openAppletTab(applet, { ...group, students: students ?? [] });
    }
    setPendingApplet(null);
  };

  const handleSelectApplet = applet => {
    if (!applet.requiresContext) {
      openAppletTab(applet);
      return;
    }
    if (studyGroup) {
      openAppletWithStudents(applet, studyGroup);
      return;
    }
    setPendingApplet(applet);
  };

  return (
    <>
      <StripWidget
        className={classes.Strip}
        image={pencilCaseImg}
        title={texts.myPencilCase}
        items={applets}
        getItemLabel={item => item.name}
        renderItem={({ item }) => <AppletCarouselItem item={item} onSelect={() => handleSelectApplet(item)} />}
      />
      {pendingApplet && !studyGroupId &&
        <SelectStudyGroupPopup onClose={() => setPendingApplet(null)} onSelect={group => openAppletWithStudents(pendingApplet, group)}/>
      }
    </>
  );
};

export default RegularAppletsWidget;